import { XCircle } from "lucide-react";
import { InputHTMLAttributes, forwardRef, useCallback } from "react";

import { Button } from "./Button";
import { InputStyles } from "./input-styles";
import { cn } from "./utils";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  removeable?: boolean;
  label?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, removeable, label, onChange, value, ...props }, ref) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
      },
      [onChange],
    );

    const handleRemove = useCallback(() => {
      if (onChange) {
        const event = {
          target: { value: "" },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    }, [onChange]);

    const hasRemoveButton = removeable && value;

    const inputElement = removeable ? (
      <div className="relative flex w-full items-center">
        <input
          type={type}
          className={cn(InputStyles.Base, InputStyles.Input, hasRemoveButton && "pr-8", className)}
          ref={ref}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {hasRemoveButton ? (
          <Button
            type="button"
            onClick={handleRemove}
            className="absolute right-2 z-[1] rounded-full"
            variant="ghost"
            size="tinyIcon"
            aria-label="Clear input"
          >
            <XCircle size={16} />
          </Button>
        ) : null}
      </div>
    ) : (
      <input
        type={type}
        className={cn(InputStyles.Base, InputStyles.Input, className)}
        ref={ref}
        value={value}
        onChange={handleChange}
        {...props}
      />
    );

    if (label) {
      return (
        <div className="flex w-full flex-col gap-1">
          <label htmlFor={props.id} className="text-sm font-medium text-gray-500">
            {label}
          </label>
          {inputElement}
        </div>
      );
    }

    return inputElement;
  },
);
Input.displayName = "Input";

export { Input };
